import { useCallback, useEffect, useMemo, useState } from "react";
import { orchestrator } from "../../../websocket/orchestrator";
import { Typography, BottomNavigation, BottomNavigationAction } from "@mui/material";
import { CalendarMonth, DeviceHub, Widgets } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { translate } from "../../../translate/translate";
import { useImportProgress } from "../../../hooks/useImportProgress";

import ImportBom from "./bom/import-bom";
import ImportStockAndPO from "./stock-and-po/import-stock-and-po";
import ImportForecast from "./forecast/import-forecast";
import ImportRFCSap from "./sap-rfc/import-rfc-sap/import-rfc-sap";
import { RfcType } from "../../../types";

type ImportMode = "xls" | "sap";

type ImportComponentProps = {
  mode: ImportMode;
};

const TAB_KEYS = ["bom", "stock", "forecast"] as const;

const getTabIndexFromKey = (tab: string | null): number => {
  const index = TAB_KEYS.indexOf((tab ?? "") as (typeof TAB_KEYS)[number]);
  return index >= 0 ? index : 0;
};

const ImportComponent = ({ mode }: ImportComponentProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    orchestrator.addConnection({ route: 'bom-imports', storageKey: 'import_bom' });
    orchestrator.addConnection({ route: 'stock-and-po-imports', storageKey: 'import_stock_and_po' });
    if (mode === 'sap') {
      orchestrator.addConnection({ route: 'sap-imports', storageKey: 'sap_import_status' });
    }
    return () => {
      orchestrator.removeConnection('import_bom');
      orchestrator.removeConnection('import_stock_and_po');
      if (mode === 'sap') orchestrator.removeConnection('sap_import_status');
    };
  }, [mode]);

  const { isImporting: isImportingBom } = useImportProgress('bom');
  const { isImporting: isImportingStockPo } = useImportProgress('stock_and_po');
  const requestedTab = searchParams.get("tab");
  const [selectedTab, setSelectedTab] = useState(() => getTabIndexFromKey(requestedTab));

  const activeImportTab = useMemo(() => {
    if (isImportingStockPo) return 1;
    if (isImportingBom) return 0;
    return null;
  }, [isImportingBom, isImportingStockPo]);

  const updateTabInUrl = useCallback((tabIndex: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("tab", TAB_KEYS[tabIndex]);
      return next;
    });
  }, [setSearchParams]);

  const handleTabChange = (_: unknown, newValue: number) => {
    setSelectedTab(newValue);
    updateTabInUrl(newValue);
  };

  useEffect(() => {
    const tabFromUrl = getTabIndexFromKey(requestedTab);
    if (tabFromUrl !== selectedTab) {
      setSelectedTab(tabFromUrl);
    }
  }, [requestedTab, selectedTab]);

  useEffect(() => {
    if (requestedTab) {
      return;
    }

    if (activeImportTab !== null) {
      if (selectedTab !== activeImportTab) {
        setSelectedTab(activeImportTab);
      }
      updateTabInUrl(activeImportTab);
      return;
    }

    updateTabInUrl(selectedTab);
  }, [activeImportTab, requestedTab, selectedTab, updateTabInUrl]);

  const sx = {
    borderTopLeftRadius: "4px",
    borderTopRightRadius: "4px",
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "4px",
    flex: "0 0 auto",
    maxWidth: "220px",
  };

  const title =
    mode === "xls"
      ? translate.t("import_spreadsheets")
      : translate.t("integration_routines_rfc_sap");

  return (
    <section>

      <div
        className="floating-title"
        style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
      >
        <Typography variant="h5">{title}</Typography>
      </div>

      <BottomNavigation
        sx={{
          gap: "8px",
          marginTop: "16px",
          display: "flex",
          justifyContent: "flex-start",
          backgroundColor: "#F0F3F6",
          height: "42px",
        }}
        showLabels
        value={selectedTab}
        onChange={handleTabChange}
      >
        <BottomNavigationAction
          label={translate.t("bom")}
          icon={<DeviceHub />}
          sx={sx}
        />

        <BottomNavigationAction
          label={translate.t("stock_and_po")}
          icon={<Widgets />}
          sx={sx}
        />

        <BottomNavigationAction
          label={translate.t("forecast")}
          icon={<CalendarMonth />}
          sx={sx}
        />
      </BottomNavigation>

      {selectedTab === 0 && (mode === "xls" ? <ImportBom /> : <ImportRFCSap rfcType={RfcType.BOM} />)}

      {selectedTab === 1 && (mode === "xls" ? <ImportStockAndPO /> : <ImportRFCSap rfcType={RfcType.STOCK_AND_PO} />)}

      {selectedTab === 2 && (mode === "xls" ? <ImportForecast /> : <ImportRFCSap rfcType={RfcType.FORECAST} />)}
    </section>
  );
};

export default ImportComponent;
